import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD, type MartinusAbacPayload } from '../permissions/abac-payloads/MartinusAbacPayload.js';
import { hasRoles } from '../permissions/api/hasRoles.js';
import { isParentOf } from '../users/family/isParentOf.js';
import { isSelf } from '../permissions/api/isSelf.js';
import { Role } from '../permissions/roles.js';
import type { UserWithPermissionFields } from '../permissions/UserWithPermissionFields.js';

/**
 * Roles which administrate SEPA mandates of other persons.
 * Mirrors the roles which hold `Permission.VIEW_OTHER_SEPA_MANDATS` (see ensure-roles.ts).
 */
const SEPA_ADMINISTRATIVE_ROLES = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.SEKRETAER, Role.PSB];

export enum SepaMandateActions {
  /**
   * Can the user view/edit mandates of other persons (administrative access).
   */
  SEPA_MANDATE_MANAGE_OTHERS = 'SEPA_MANDATE_MANAGE_OTHERS',

  /**
   * Can the user edit the given mandate (fields, accept, end, reactivate).
   * Needs the `mandat` context in the payload.
   */
  SEPA_MANDATE_EDIT = 'SEPA_MANDATE_EDIT',

  /**
   * Can the user change the accepting person of a mandate (take over / reassign).
   * This is an advanced administrative action.
   */
  SEPA_MANDATE_CHANGE_ACCEPTOR = 'SEPA_MANDATE_CHANGE_ACCEPTOR',

  /**
   * Can the user delete a mandate. Only admins (granted via AdminPolicy).
   */
  SEPA_MANDATE_DELETE = 'SEPA_MANDATE_DELETE',

  /**
   * Can reframe the sepa mandate to the values of the group.
   * Normally an accepted sepa mandate can not be altered except if a user overwrite the values for a specific booking.
   * This is by design to ensure data integrity and prevent accidental changes.
   * The overwrites are only allowed to alter prices per month on dynamic pricing and maybe alter the month and time of booking for families with financial difficulties for example.
   * The action to reset a mandate to the current values of a group is a fallback action to correct error (e.g. wrong month was entered, forgot to export things, little migrations, etc.).
   * Only admins (granted via AdminPolicy) can perform this action.
   */
  SEPA_MANDATE_REFRAME = 'SEPA_MANDATE_REFRAME',
}

export interface SepaMandateAbacPayload extends MartinusAbacPayload {
  /**
   * Permission relevant fields of the mandate which is acted upon.
   * Keep this a plain object so payload hashing stays stable.
   */
  mandat?: Maybe<{
    accepted: Maybe<boolean>;
    acceptedByID: Maybe<string>;
  }>;

  /**
   * Extends the default payload with the user that is being viewed
   */
  viewedUser: Maybe<UserWithPermissionFields>;
}

const SepaMandatePolicyFunctions: Record<SepaMandateActions, LoggedInEvaluationFunction<SepaMandateAbacPayload>> = {
  /**
   * Administrative SEPA roles may view and manage mandates of other persons.
   */
  [SepaMandateActions.SEPA_MANDATE_MANAGE_OTHERS]: async request => {
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: false, reason: 'Nutzer nicht seine eigenen Mandate managen.' };
    }

    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: false, reason: 'Nutzer ist Sorgeberechtigter des Mandatsinhabers.' };
    }

    if (hasRoles({ user: request.payload.currentUser, roles: SEPA_ADMINISTRATIVE_ROLES }))
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit administrativem SEPA-Zugriff.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit administrativem SEPA-Zugriff.' };
  },

  /**
   * A mandate may be edited if:
   * - it is not yet accepted (template / pending acceptance), or
   * - the current user is the person who accepted it, or
   * - the current user has administrative SEPA access.
   */
  [SepaMandateActions.SEPA_MANDATE_EDIT]: async request => {
    const mandat = request.payload.mandat;

    if (hasRoles({ user: request.payload.currentUser, roles: SEPA_ADMINISTRATIVE_ROLES }))
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit administrativem SEPA-Zugriff.' };

    if (!mandat?.accepted) return { allowed: true, reason: 'Das Mandat wurde noch nicht angenommen.' };

    if (mandat.acceptedByID === request.payload.currentUser._id)
      return { allowed: true, reason: 'Nutzer hat das Mandat selbst angenommen.' };

    return { allowed: false, reason: 'Das Mandat wurde von einer anderen Person angenommen.' };
  },

  /**
   * Changing the accepting person is reserved for billing-administrative roles.
   */
  [SepaMandateActions.SEPA_MANDATE_CHANGE_ACCEPTOR]: async request => {
    if (hasRoles({ user: request.payload.currentUser, roles: [Role.SPENDEN_ADMIN, Role.BUCHHALTUNG] }))
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung zum Wechsel der akzeptierenden Person.' };

    return {
      allowed: false,
      reason: 'Nutzer hat keine Rolle mit Berechtigung zum Wechsel der akzeptierenden Person.',
    };
  },

  /**
   * Deleting mandates is exclusively an admin operation (allowed via the AdminPolicy).
   */
  [SepaMandateActions.SEPA_MANDATE_DELETE]: async () => {
    return { allowed: false, reason: 'Nur Admins dürfen SEPA-Mandate löschen.' };
  },

  [SepaMandateActions.SEPA_MANDATE_REFRAME]: async request => {
    if (hasRoles({ user: request.payload.currentUser, roles: [Role.SPENDEN_ADMIN, Role.BUCHHALTUNG] }))
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung zum Neuerstellen des SEPA-Mandats.' };

    return {
      allowed: false,
      reason: 'Nutzer hat keine Rolle mit Berechtigung zum Neuerstellen des SEPA-Mandats.',
    };
  },
};

export const SepaMandatePolicy: IPolicy<SepaMandateAbacPayload> = {
  name: 'SepaMandatePolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await SepaMandatePolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
